/**
 * SSD Intranet Role Permissions & Navigation Configuration
 */

import type { MenuItem, CategoryAccessRule, UserRole } from '../types/roles';

/**
 * Navigation Menu Configuration
 * Controls items shown in primary navigation and dropdowns based on user roles
 */
export const DEFAULT_MENU_ITEMS: MenuItem[] = [
  {
    id: 'management-reports',
    label: 'Management Hub',
    path: '/management',
    iconName: 'chart',
    allowedRoles: ['Management', 'Admin'],
  },
  {
    id: 'admin-panel',
    label: 'Admin Controls',
    path: '/admin',
    iconName: 'cog',
    allowedRoles: ['Admin'],
  },
];

/**
 * Role-Based Restricted Categories Rules
 * Categories listed here will only be visible to users holding at least one allowed role.
 * Unlisted categories are accessible to all roles by default.
 */
export const RESTRICTED_CATEGORY_RULES: CategoryAccessRule[] = [
  {
    category: 'Management',
    allowedRoles: ['Management', 'Admin'],
  },
  {
    category: 'Financials',
    allowedRoles: ['Management', 'Admin'],
  },
  {
    category: 'Billing Admin',
    allowedRoles: ['Management', 'Admin', 'Staff'],
  },
  {
    category: 'Clinical Governance',
    allowedRoles: ['Provider', 'Management', 'Admin'],
  },
  {
    category: 'SuperAdmin',
    allowedRoles: ['Admin'],
  },
];

/**
 * List of Superadmin Emails authorized to access the Admin Control Panel
 */
export const AUTHORIZED_ADMIN_EMAILS = [
  'agonzalez@ssdspc.com',
  'admin@ssdspc.com',
  'mm@ssdspc.com',
];

/**
 * Default Team Mappings for Domain Users
 * Maps specific email prefixes / patterns or roles to primary teams for default routing
 */
export const DEFAULT_ROLE_MAPPINGS: Record<string, { roles: UserRole[]; primaryTeam: string; assignedTeams: string[] }> = {
  'agonzalez@ssdspc.com': { roles: ['Admin', 'Management'], primaryTeam: 'SuperAdmin', assignedTeams: ['SuperAdmin', 'Admin Team'] },
  'admin@ssdspc.com': { roles: ['Admin', 'Management'], primaryTeam: 'SuperAdmin', assignedTeams: ['SuperAdmin', 'Admin Team'] },
  'mm@ssdspc.com': { roles: ['Admin', 'Management'], primaryTeam: 'SuperAdmin', assignedTeams: ['SuperAdmin', 'Operations'] },
  'automation@ssdspc.com': { roles: ['Admin', 'Staff'], primaryTeam: 'SuperAdmin', assignedTeams: ['SuperAdmin'] },
  'ehenegar@ssdspc.com': { roles: ['Staff'], primaryTeam: 'Unassigned', assignedTeams: ['Unassigned'] },
};
